import { useState } from "react"
import { ExternalLink, FileText } from "lucide-react"
import { cn } from "@/lib/cn"

// Per-message file attachment. Three render modes by content type:
//   image/*  → small thumbnail; click expands inline to ~400px; an
//              ↗ overlay opens the original in a new tab.
//   video/*  → small <video> preview with controls; same expand behavior.
//   anything else → file-icon chip with filename + size; click downloads.
//
// Inline expansion stays inside the chat column so the operator doesn't
// lose context. The new-window arrow handles the "I want to zoom + drag
// around" case.

type FileLike = {
  filename: string
  contentType: string
  byteSize: number
  url: string | null
  thumbnailUrl: string | null
}

type Props = { file: FileLike }

export function MessageAttachment({ file }: Props) {
  const [expanded, setExpanded] = useState(false)

  const isImage = file.contentType?.startsWith("image/")
  const isVideo = file.contentType?.startsWith("video/")
  const sizeKb  = (file.byteSize / 1024).toFixed(1)

  if (!file.url) {
    return (
      <span className="inline-flex items-center gap-1 rounded-chip bg-white/10 px-2 py-0.5 text-xs text-app-muted">
        <FileText size={12} /> {file.filename}
      </span>
    )
  }

  // Expanded preview cap: max 400px tall AND ≤ 95vw wide. The 95vw
  // guard is what makes this safe on a 360-390px phone — without it the
  // expanded image overflows the viewport and pushes the page sideways.
  const expandedSize = "max-h-[400px] max-w-[min(400px,95vw)]"

  if (isImage) {
    return (
      <div className="inline-block">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="group relative block overflow-hidden rounded-control border border-border bg-app-panel2"
          title={file.filename}
        >
          <img
            src={file.thumbnailUrl ?? file.url}
            alt={file.filename}
            loading="lazy"
            className={cn(
              "block object-cover transition-all",
              expanded ? expandedSize : "h-20 w-20",
            )}
          />
          <OpenInNewWindow url={file.url} />
        </button>
      </div>
    )
  }

  if (isVideo) {
    return (
      <div className="inline-block">
        <div
          className={cn(
            "group relative overflow-hidden rounded-control border border-border bg-black",
            expanded ? expandedSize : "h-20 w-20",
          )}
          onClick={() => setExpanded((v) => !v)}
        >
          <video
            src={file.url}
            controls={expanded}
            muted={!expanded}
            preload="metadata"
            className={cn("block h-full w-full cursor-pointer", expanded ? "object-contain" : "object-cover")}
          />
          <OpenInNewWindow url={file.url} />
        </div>
      </div>
    )
  }

  return (
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-chip border border-border bg-app-panel2 px-2 py-1 text-xs text-app-text hover:bg-white/10"
      title={file.filename}
    >
      <FileText size={12} />
      <span className="max-w-[200px] truncate">{file.filename}</span>
      <span className="text-app-subtle">{sizeKb} KB</span>
    </a>
  )
}

// Floating ↗ button overlaid in the top-right corner of an attachment
// preview. Click stops propagation so it doesn't toggle the parent expand.
function OpenInNewWindow({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur-sm transition group-hover:opacity-100"
      title="Open in new window"
      aria-label="Open in new window"
    >
      <ExternalLink size={12} />
    </a>
  )
}
